import { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import DownArrow from '../../assets/down-arrow.png';
import { addElipsesToText, getIdAttributes } from '../../utils/helper';
import { useTranslation, Trans } from 'react-i18next';
import { MultiSelectDropdownProps } from './multiSelectDropdown.type';

export const idPrefix = 'dropdown-input';

// --------------------
// Helper
// --------------------

const getSelectedLabels = <T extends string | number>(
  props: Pick<MultiSelectDropdownProps<T>, 'isMulti' | 'selected' | 'options'>,
): string[] => {
  const { isMulti, selected, options = [] } = props;

  if (isMulti && Array.isArray(selected)) {
    return options
      .filter((opt) => selected.includes(opt.value))
      .map((opt) => opt.label);
  }

  const found = options.find((opt) => opt.value === selected);
  return found ? [found.label] : [];
};

// --------------------
// Button
// --------------------

const DropdownButton = <T extends string | number>({
  disabled,
  onClick,
  errorMessage,
  buttonClassName,
  placeholder,
  selectedLabels,
  isOpen,
  isMulti,
}: Pick<
  MultiSelectDropdownProps<T>,
  'disabled' | 'errorMessage' | 'buttonClassName' | 'placeholder' | 'isMulti'
> & {
  onClick: () => void;
  selectedLabels: string[];
  isOpen: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={[
        'w-full flex items-center justify-between h-[40px] px-4 py-2 rounded-full text-sm transition-colors',
        disabled ? 'bg-[#E5E5E5] cursor-not-allowed' : 'bg-[#FAFAFA]',
        errorMessage ? 'border border-[#B60000]' : '',
        !errorMessage && isOpen ? 'border border-[#561BDC]' : '',
        !errorMessage && !isOpen ? 'border border-transparent' : '',
        buttonClassName,
      ]
        .filter(Boolean)
        .join(' ')}
      {...getIdAttributes(idPrefix, 'button')}
    >
      <span
        className={`${
          selectedLabels.length > 0 ? 'text-black' : 'text-gray-400'
        } min-h-[20px]`}
      >
        {selectedLabels.length > 0
          ? addElipsesToText(selectedLabels.map((l) => t(l)).join(', '), 60) +
            ' ' +
            (isMulti && selectedLabels.length > 0
              ? `(${selectedLabels.length})`
              : '')
          : placeholder}
      </span>

      <img src={DownArrow} className="w-[11px] h-[6px]" />
    </button>
  );
};

// --------------------
// Dropdown Panel
// --------------------

const DropdownPanel = <T extends string | number>({
  props,
  setIsOpen,
  handleSelect,
  isSelected,
  popperRef,
  popperStyle,
  popperAttributes,
}: {
  props: MultiSelectDropdownProps<T>;
  setIsOpen: (open: boolean) => void;
  handleSelect: (value: T) => void;
  isSelected: (value: T) => boolean;
  popperRef: (el: HTMLDivElement | null) => void;
  popperStyle: React.CSSProperties;
  popperAttributes: Record<string, string> | undefined;
}) => {
  const { isMulti, options = [], search } = props;

  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = useMemo(
    () =>
      options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [options, searchTerm],
  );

  return (
    <div
      ref={popperRef}
      style={popperStyle}
      {...popperAttributes}
      className="z-50 bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
    >
      {search && (
        <div className="p-2 border-b">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border rounded-full focus:border-[#561BDC]"
          />
        </div>
      )}

      <div className="max-h-72 overflow-y-auto">
        {filteredOptions.map((option) => {
          const selectedItem = isSelected(option.value);

          return (
            <div
              key={String(option.value)}
              className={`flex items-center gap-2 px-4 py-2 text-sm cursor-pointer hover:bg-[#E2E3FF]`}
              onClick={() => handleSelect(option.value)}
            >
              {isMulti && (
                <input
                  type="checkbox"
                  checked={selectedItem}
                  readOnly
                  className="form-checkbox rounded border-gray-300 accent-THEME_COLOR"
                />
              )}

              <span>
                <Trans i18nKey={option.label} />
              </span>

              {selectedItem && !isMulti && (
                <span className="ml-auto">&#10003;</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --------------------
// Main Component
// --------------------

const MultiSelectDropdown = <T extends string | number>(
  props: MultiSelectDropdownProps<T>,
) => {
  const [isOpen, setIsOpen] = useState(false);

  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  const { selected, isMulti, disabled, errorMessage, onSelect } = props;

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: { offset: [0, 8] },
      },
    ],
  });

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!isOpen) return;
      const target = event.target as Node;

      if (
        !referenceElement?.contains(target) &&
        !popperElement?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, referenceElement, popperElement]);

  const handleSelect = (value: T) => {
    if (!onSelect || disabled) return;

    if (isMulti) {
      const current = Array.isArray(selected) ? selected : [];
      const newSelection = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];

      onSelect(newSelection as T[]);
    } else {
      onSelect(value);
      setIsOpen(false);
    }
  };

  const isSelected = (value: T) =>
    isMulti
      ? Array.isArray(selected) && selected.includes(value)
      : selected === value;

  return (
    <div
      className="relative w-full"
      ref={setReferenceElement}
      {...getIdAttributes(idPrefix, 'container')}
    >
      <DropdownButton
        {...props}
        selectedLabels={getSelectedLabels(props)}
        isOpen={isOpen}
        isMulti={isMulti}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
      />

      {errorMessage && !isOpen && (
        <div className="text-[#B60000] text-sm mt-1">{errorMessage}</div>
      )}

      {isOpen &&
        !disabled &&
        ReactDOM.createPortal(
          <DropdownPanel
            props={props}
            setIsOpen={setIsOpen}
            handleSelect={handleSelect}
            isSelected={isSelected}
            popperRef={setPopperElement}
            popperStyle={{
              ...styles.popper,
              width: referenceElement?.offsetWidth,
              zIndex: 9999,
            }}
            popperAttributes={attributes.popper}
          />,
          document.body,
        )}
    </div>
  );
};

export default MultiSelectDropdown;
